/**
 * `/leads` — Relay Leads pipeline dashboard.
 *
 * Shows analytics summary + a paginated list of leads with status filters.
 * Also provides an "Inbox" tab that navigates to the conversation-centric view.
 * Links to lead detail and new-lead capture form.
 */
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLeadsAnalytics, useLeadsList } from "@/hooks/use-leads";
import type { LeadResponse } from "@/lib/api-client";
import { formatTimestamp } from "@/lib/formatters";

const STATUS_LABELS: Record<LeadResponse["status"], string> = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  booked: "Booked",
  closed: "Closed",
  lost: "Lost",
};

const STATUS_COLORS: Record<LeadResponse["status"], string> = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  qualified: "bg-purple-100 text-purple-800",
  booked: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-600",
  lost: "bg-red-100 text-red-700",
};

function StatusBadge({ status }: { status: LeadResponse["status"] }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function AnalyticsBar() {
  const analytics = useLeadsAnalytics();

  if (analytics.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!analytics.data) return null;

  const { total, status_counts } = analytics.data;
  const highlights: Array<{ label: string; value: number; status?: LeadResponse["status"] }> = [
    { label: "Total", value: total },
    { label: "New", value: status_counts.new ?? 0, status: "new" },
    { label: "Qualified", value: status_counts.qualified ?? 0, status: "qualified" },
    { label: "Booked", value: status_counts.booked ?? 0, status: "booked" },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {highlights.map(({ label, value, status }) => (
        <Card key={label} className="border-0 bg-gray-50 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
            {status && <StatusBadge status={status} />}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

const ALL_STATUSES = ["all", "new", "contacted", "qualified", "booked", "closed", "lost"] as const;
type FilterStatus = (typeof ALL_STATUSES)[number];

// "inbox" is a special view tab that navigates to /leads/inbox rather than filtering.
type ViewTab = "pipeline" | "inbox";

const PAGE_LIMIT = 25;

export function LeadsHomePage() {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState<ViewTab>("pipeline");
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allItems, setAllItems] = useState<LeadResponse[]>([]);
  // Tracks whether the next leads.data update is a "load more" append vs. a fresh load.
  const isLoadMoreRef = useRef(false);

  const leads = useLeadsList({
    limit: PAGE_LIMIT,
    ...(cursor !== undefined && { cursor }),
    ...(filter !== "all" && { status: filter as LeadResponse["status"] }),
  });

  useEffect(() => {
    if (!leads.data) return;
    if (isLoadMoreRef.current) {
      setAllItems((prev) => [...prev, ...(leads.data?.items ?? [])]);
      isLoadMoreRef.current = false;
    } else {
      setAllItems(leads.data.items);
    }
  }, [leads.data]);

  function handleFilterChange(v: FilterStatus) {
    setFilter(v);
    setCursor(undefined);
    setAllItems([]);
    isLoadMoreRef.current = false;
  }

  function handleLoadMore() {
    if (!leads.data?.next_cursor) return;
    isLoadMoreRef.current = true;
    setCursor(leads.data.next_cursor);
  }

  const isInitialLoading = leads.isLoading && allItems.length === 0;
  const isFetchingMore = leads.isFetching && allItems.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Module</p>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Leads</h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Capture inbound leads, track status, and manage conversations.
          </p>
        </div>
        <Link to="/leads/new">
          <Button>New lead</Button>
        </Link>
      </header>

      <AnalyticsBar />

      <div>
        {/* Top-level view switcher: Pipeline vs Inbox */}
        <Tabs
          value={activeView}
          onValueChange={(v) => {
            const tab = v as ViewTab;
            setActiveView(tab);
            if (tab === "inbox") navigate("/leads/inbox");
          }}
          className="mb-2"
        >
          <TabsList>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="inbox">Inbox</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Status filter tabs — only shown in Pipeline view */}
        <Tabs value={filter} onValueChange={(v) => handleFilterChange(v as FilterStatus)}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="new">New</TabsTrigger>
            <TabsTrigger value="contacted">Contacted</TabsTrigger>
            <TabsTrigger value="qualified">Qualified</TabsTrigger>
            <TabsTrigger value="booked">Booked</TabsTrigger>
            <TabsTrigger value="closed">Closed</TabsTrigger>
            <TabsTrigger value="lost">Lost</TabsTrigger>
          </TabsList>
        </Tabs>

        {isInitialLoading && (
          <div className="flex flex-col gap-2" data-testid="leads-loading">
            {Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders never reorder
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        )}

        {leads.isError && (
          <p className="text-sm text-destructive" data-testid="leads-error">
            Failed to load leads. Please try again.
          </p>
        )}

        {!isInitialLoading && allItems.length === 0 && !leads.isError && (
          <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-gray-200 p-12 text-center" data-testid="leads-empty">
            <p className="text-sm text-muted-foreground">
              {filter === "all" ? "No leads yet." : `No ${STATUS_LABELS[filter as LeadResponse["status"]]} leads.`}
            </p>
            <Link to="/leads/new">
              <Button variant="outline" size="sm">Capture first lead</Button>
            </Link>
          </div>
        )}

        {allItems.length > 0 && (
          <>
            <div className="flex flex-col divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white overflow-hidden" data-testid="leads-list">
              {allItems.map((lead) => (
                <Link
                  key={lead.id}
                  to={`/leads/${lead.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {lead.source ?? "Direct"} — #{lead.id.slice(0, 8)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(lead.created_at)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {lead.assigned_to && (
                      <span className="hidden text-xs text-muted-foreground sm:block truncate max-w-32">
                        {lead.assigned_to}
                      </span>
                    )}
                    <StatusBadge status={lead.status} />
                  </div>
                </Link>
              ))}
            </div>

            {leads.data?.next_cursor && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isFetchingMore}
                  data-testid="leads-load-more"
                >
                  {isFetchingMore ? "Loading…" : "Load more"}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default LeadsHomePage;
